import React, { useCallback, useEffect, useState } from 'react';
import * as yup from 'yup';
import { ValidationError } from 'yup';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { useService } from '../../services/services';
import { route } from '../../services/utils';
import Group from '../../components/form/elements/group';
import Text from '../../components/form/elements/text';
import Checkbox from '../../components/form/elements/checkbox';
import Link from '../../components/form/elements/link';
import Form, { SubmitType } from '../../components/form';
import Button from '../../components/form/elements/button';
import Farm, { Sector } from '../../interfaces/farm';
import { FARM_VIEW } from '../../router/routes';
import { useObservableState } from 'observable-hooks';
import { map } from 'rxjs/operators';
import Disabled from '../../components/form/elements/disabled';
import TextArea from '../../components/form/elements/text-area';
import Warning from '../../components/form/elements/warning';
import Address from '../../components/form/elements/address';
import { useDispatch } from 'react-redux';
import { farm as setFarm } from '../../store/global-slice';

export default function FarmEdit(this: any) {
  const history = useHistory();
  const { t } = useTranslation();
  const { farmService } = useService();

  const { farmId } = useParams<{ farmId: string }>();
  const dispatch = useDispatch();

  const [farm] = useObservableState<Farm>(
    () =>
      farmService.show(farmId).pipe(
        map((farm: any) => {
          farm.sectors = Object.fromEntries(
            (farm.sectors as Sector[]).map((value) => [value, true])
          ) as { [key in Sector]: boolean };

          farm.legal_address = {
            value: farm.legal_address,
            label: farm.legal_address,
          };

          return farm;
        })
      ),
    null
  );

  const [bodyElements, setBodyElements] = useState([]);
  const [footerElements, setFooterElements] = useState([]);

  const submit = useCallback<SubmitType<Farm>>(
    (form: any) => {
      form.legal_address = form.legal_address?.value;

      return [
        farmService.update(farm.id, form),
        (farm) => {
          dispatch(setFarm(farm));
          history.push(route(FARM_VIEW, { farmId: farm.id }));
        },
      ];
    },
    [farm, farmService, history, dispatch]
  );

  const [farmSchema] = useState(
    yup
      .object()
      .shape({
        vat: yup.boolean().default(false),
        sectors: yup.object().shape({
          crop: yup.boolean().default(false),
          livestock: yup.boolean().default(false),
        }),
        comment: yup.string().nullable(),
        legal_address: yup.lazy((addressValue) => {
          if (typeof addressValue === 'object') {
            return yup.object().shape({
              value: yup.string().required(),
            });
          } else {
            return yup.string().max(255).required();
          }
        }),
      })
      .test({
        name: 'is-required-one-of',
        message: t('pages.farm.messages.is-required-one-of-sectors-to-be-true'),
        test: function (values): ValidationError | boolean {
          const isValid = ['warehouse'].some((field) => {
            if (Array.isArray(values.sectors)) {
              return values.sectors.includes(field);
            } else {
              return values.sectors[field];
            }
          });

          // TODO - Make this show error

          return isValid
            ? true
            : this.createError({
                message: 'Crop or livestock required!',
              });
        },
      })
  );

  useEffect(() => {
    if (farm !== null) {
      setBodyElements([
        Group([
          Disabled([
            Text('name', t('pages.farm.name')),
            Text('registration_number', t('pages.farm.registration-number')),
          ]),
          Address('legal_address', t('legal_address')),
          Checkbox('vat', t('pages.farm.vat')),
          Disabled([Checkbox('sectors.warehouse', t('pages.farm.warehouse'))]),
          Checkbox('sectors.crop', t('pages.farm.crop')),
          Checkbox('sectors.livestock', t('pages.farm.livestock')),
          Text('bank_name', t('pages.farm.bank-name')),
          Text('account_number', t('pages.farm.account-number')),
          Warning(t('pages.farm.changing_sectors')),
          TextArea('comment', t('pages.farm.comment')),
        ]),
      ]);

      setFooterElements([
        Button('save'),
        Link(route(FARM_VIEW, { farmId: farm.id }), 'cancel'),
      ]);
    }
  }, [farm, farmService, t, setBodyElements, setFooterElements]);

  return (
    <Form
      mode={'edit'}
      title={t('pages.farm.title')}
      backTo={null}
      body={bodyElements}
      footer={footerElements}
      values={farm}
      submit={submit}
      schema={farmSchema}
    />
  );
}
